import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA} from '@angular/material/legacy-dialog';
import { flatMap } from 'rxjs/operators';
import { SharedConfirmData } from '../../shared/components/modals/confirmation-data.model';
import { getCurrentDateToString, getServerDate } from '../../shared/helpers/time-helpers.model';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { CustomerManageService } from '../shared/customer-manage.service';
import { CustomerModalBaseComponent } from '../shared/customer-modal-base.component';
import { CustomerRepresentative } from './customer-representative.model';

export class CustomerRepresentativeContext {
    constructor(
        public customerRepresentativeId: number,
        public customerNumberId: number
    ) { }
}

@Component({
    selector: 'customer-representative-modal',
    templateUrl: './customer-representative-modal.html'
})
export class CustomerRepresentativeModalComponent extends CustomerModalBaseComponent {
    public customerRepresentativeId: number;
    public customerRepresentative: CustomerRepresentative;
    public isSubmitted = false;
    public representativeForm: UntypedFormGroup;
    public showButtonLoading = false;
    public passwordMinLength = 8;
    public passwordMaxLength = 16;
    public characterClassMinCount = 3;

    constructor(
        private location: PlatformLocation,
        private fb: UntypedFormBuilder,
        private service: CustomerManageService,
        private dialogRef: MatDialogRef<CustomerRepresentativeModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) private data: CustomerRepresentativeContext,
        public modalService: MvdModalService
    ) {
        super(modalService);
        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    public ngOnInit(): void {
        this.customerRepresentativeId = this.data.customerRepresentativeId;
        const passwordValidators = [Validators.minLength(this.passwordMinLength), Validators.maxLength(this.passwordMaxLength), this.passwordCharacterClassCountValidator];
        if (!this.customerRepresentativeId) {
            passwordValidators.push(Validators.required);
        }

        this.representativeForm = this.fb.group({
            'firstName': ['', Validators.required],
            'lastName': ['', Validators.required],
            'email': ['', Validators.required],
            'driverLicenseNumber': [null],
            'cellPhone': ['', Validators.required],
            'effectiveDate': [getCurrentDateToString(), Validators.required],
            'organizationAdministrator': [false],
            'mvdNowPassword': [''],
            'confirmMVDNowPassword': [''],
        }, {
            validator: this.passwordMatchValidator
        });

        this.customerRepresentative = new CustomerRepresentative();
        if (this.customerRepresentativeId != 0) {
            this.initCustomerRepresentative();
        }
    }

    private passwordCharacterClassCountValidator = (control: UntypedFormControl): ValidationErrors => {
        const password = control.value;
        if (!password) {
            return null;
        }

        const lowercaseIndicator = /[a-z]/.test(password) ? 1 : 0;
        const uppercaseIndicator = /[A-Z]/.test(password) ? 1 : 0;
        const numberIndicator = /[0-9]/.test(password) ? 1 : 0;
        const symbolIndicator = /[@#\$%\^&\*\-_!\+=\[\]\{\}\|\\:',\.\?\/`~"\(\);<>]/.test(password) ? 1 : 0;

        const characterClassCount = lowercaseIndicator + uppercaseIndicator + numberIndicator + symbolIndicator;
        if  (characterClassCount < this.characterClassMinCount) {
            return { 'passwordRequiresMoreCharacterClasses': true };
        }
    };

    private passwordMatchValidator(group: UntypedFormGroup): ValidationErrors | null {
        const mvdNowPassword = group.get('mvdNowPassword') ? group.get('mvdNowPassword').value : null;
        const confirmMVDNowPassword = group.get('confirmMVDNowPassword') ? group.get('confirmMVDNowPassword').value : null;
        if (mvdNowPassword && mvdNowPassword != confirmMVDNowPassword) {
            return { 'passwordMatch': true };
        }
    }

    private initCustomerRepresentative(): void {
        this.service.getCustomerRepresentative(this.customerRepresentativeId)
            .subscribe(result => {
                this.customerRepresentative = result;
                this.customerRepresentative.editExistingUser = true;

                const effectiveDate = getServerDate(this.customerRepresentative, 'effectiveDate');
                this.representativeForm.get('effectiveDate').setValue(effectiveDate);
                this.representativeForm.get('firstName').setValue(this.customerRepresentative.firstName);
                this.representativeForm.get('lastName').setValue(this.customerRepresentative.lastName);
                this.representativeForm.get('email').setValue(this.customerRepresentative.email);
                this.representativeForm.get('driverLicenseNumber').setValue(this.customerRepresentative.driverLicenseNumber);
                this.representativeForm.get('cellPhone').setValue(this.customerRepresentative.cellPhone);
                this.representativeForm.get('organizationAdministrator').setValue(this.customerRepresentative.organizationAdministrator);
            },
            e => this.handleValidationResult(e)
            );
    }

    public deleteCustomerRepresentative(): void {
        const itemType = 'customer representative';
        this.endDateRecordConfirmation(itemType)
            .pipe(
                flatMap(() => {
                    const model = this.getModel();
                    model.endDate = getCurrentDateToString();
                    model.deleteExistingUser = true;

                    return this.service.saveCustomerRepresentative(model);
                })
            )
            .subscribe(
                () => {
                    this.showButtonLoading = false;
                    this.dialogRef.close(true);
                },
                e => {
                    this.showButtonLoading = false;
                    this.handleValidationResult(e);
                }
            );
    }

    public cancel(): void {
        this.dialogRef.close();
    }

    public onSaveClick(): void {
        this.isSubmitted = true;
        if (this.representativeForm.valid) {
            this.service.unassignedEmailExists(this.representativeForm.get('email').value)
                .subscribe(result => {
                    if (result) {
                        const confirmData = new SharedConfirmData();
                        confirmData.title('Confirm');
                        confirmData.body('A user with this email already exists. Do you want to link this customer representative record to this user? Note that the password you choose here may not represent the password for this user.');
                        confirmData.cancelBtn('No');
                        confirmData.okBtn('Yes');
                        this.modalService.confirmModal(confirmData).afterClosed()
                            .subscribe(confirmed => {
                                if (confirmed) {
                                    const mapExistingUser = true;
                                    this.saveCustomerRepresentative(mapExistingUser);
                                }
                            });
                    } else {
                        this.saveCustomerRepresentative();
                    }
                });
        }
    }

    private getModel(mapExistingUser: boolean = null): CustomerRepresentative {
        const model = Object.assign(new CustomerRepresentative(), this.customerRepresentative);

        model.customerNumberID = this.data.customerNumberId;
        model.id = this.customerRepresentativeId;
        model.mapExistingUser = !!mapExistingUser;

        model.firstName = this.representativeForm.get('firstName').value;
        model.lastName = this.representativeForm.get('lastName').value;
        model.email = this.representativeForm.get('email').value;
        const driverLicenseNumber = this.representativeForm.get('driverLicenseNumber').value;
        model.driverLicenseNumber = driverLicenseNumber ? driverLicenseNumber.trim() : null;
        model.cellPhone = this.representativeForm.get('cellPhone').value;
        model.effectiveDate = this.representativeForm.get('effectiveDate').value;
        model.organizationAdministrator = this.representativeForm.get('organizationAdministrator').value;
        model.mvdNowPassword = this.representativeForm.get('mvdNowPassword').value;
        model.confirmMVDNowPassword = this.representativeForm.get('confirmMVDNowPassword').value;

        return model;
    }

    private saveCustomerRepresentative(mapExistingUser: boolean = null): void {
        this.showButtonLoading = true;
        this.service.saveCustomerRepresentative(this.getModel(mapExistingUser))
            .subscribe(result => {
                this.isSubmitted = false;
                this.showButtonLoading = false;
                this.dialogRef.close(true);
            },
            e => {
                this.showButtonLoading = false;
                this.handleValidationResult(e);
            }
            );
    }
}
